import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { CoinIconXsmall } from "../../ReusableComponents/SvgAndIcons/SvgIcons";
import { promptList } from "../../Utilities/MidjourneyPromptList";
import useStyles from "./SequenceThumbnailGenerateModal.styles";
import { X } from "@phosphor-icons/react";

type Props = {
  handleSequenceThumbnailModal: () => void;
  shotDescription: string;
  handleSubmit: (
    promptStyleIndex: any,
    description: string,
    relatedArtifactIds: any[],
  ) => void;
  shotListLength: number;
  isThumbnailGenerating: boolean;
  selectedArtifacts: any;
};

const SequenceThumbnailGenerateModal = ({
  handleSequenceThumbnailModal,
  handleSubmit,
  shotDescription,
  shotListLength,
  isThumbnailGenerating,
  selectedArtifacts,
}: Props) => {
  const classes = useStyles();

  const noImageUrl =
    "https://images.unsplash.com/photo-1692607038324-6957c392410c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1932&q=80";

  const [selectedPrompt, setSelectedPrompt] = useState<any>();
  const [description, setDescription] = useState<string>(shotDescription || "");
  const [relatedArtifactIds, setRelatedArtifactIds] = useState<
    { shotId: string; artifactIds: string[] }[]
  >([]);

  useEffect(() => {
    // relatedArtifactIds: { shotId: string, artifactIds: string[] }[] — one entry per shot
    if (!selectedArtifacts || selectedArtifacts.length === 0) {
      setRelatedArtifactIds([]);
      return;
    }

    const byShot: Record<string, string[]> = {};
    selectedArtifacts.forEach((item: any) => {
      const id = item?.shotId;
      const artifactId = item?.artifact?.artifactId;
      if (!id || !artifactId) return;
      if (!byShot[id]) byShot[id] = [];
      if (!byShot[id].includes(artifactId)) byShot[id].push(artifactId);
    });
    const ids = Object.entries(byShot).map(([shotId, artifactIds]) => ({
      shotId,
      artifactIds,
    }));
    setRelatedArtifactIds(ids);
  }, [selectedArtifacts]);

  const handleRemoveArtifact = (artifactId: string, shotId: string) => {
    setRelatedArtifactIds((prev) =>
      prev
        .map((entry) =>
          entry.shotId === shotId
            ? {
                ...entry,
                artifactIds: entry.artifactIds.filter(
                  (id: string) => id !== artifactId,
                ),
              }
            : entry,
        )
        .filter((entry) => entry.artifactIds.length > 0),
    );
  };

  return (
    <Container className={classes.container}>
      <Typography className={classes.header}>
        Re-Generate Sequence Thumbnails
      </Typography>
      <Box className={classes.containerTwo}>
        <Box>
          <Typography className={classes.labelText}>Description</Typography>
          <TextField
            autoFocus={false}
            placeholder="Add your description here"
            className={classes.textField}
            fullWidth
            multiline
            rows={2}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Box>
        <Box>
          <Typography className={classes.labelText}>
            Story Board Style (choose one style)
          </Typography>
          <Box className={classes.styleBoardContainer}>
            {promptList.map((prompt, index) => {
              return (
                <div
                  className={
                    selectedPrompt?.id === prompt.id
                      ? classes.selectedStyleBoard
                      : classes.styleBoard
                  }
                  key={prompt.id}
                  onClick={() => setSelectedPrompt(prompt)}
                >
                  <img
                    src={prompt?.image ? prompt.image : noImageUrl}
                    className={classes.promptImage}
                  />
                  <Typography className={classes.promptName}>
                    {prompt.name}
                  </Typography>
                </div>
              );
            })}
          </Box>
        </Box>
        {selectedArtifacts.length > 0 && (
          <Box>
            <Typography className={classes.labelText}>
              Selected Artifacts
            </Typography>
            {selectedArtifacts && selectedArtifacts.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  width: "100%",
                  mt: 1,
                }}
              >
                {selectedArtifacts.map(
                  ({
                    artifact,
                    shotId,
                    keyword,
                    shotNumberText,
                  }: {
                    shotId: string;
                    artifact: any;
                    keyword: any;
                    shotNumberText: string;
                  }) => {
                    const firstImage = artifact.artifactReferenceImages[0];
                    if (!firstImage) return null;
                    if (
                      !relatedArtifactIds.some(
                        (id) =>
                          id.shotId === shotId &&
                          id.artifactIds.includes(artifact.artifactId),
                      )
                    )
                      return null;
                    return (
                      <Tooltip
                        key={`${shotId}-${artifact.artifactId}`}
                        title={`${keyword} - ${shotNumberText}`}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={firstImage.signedUrl}
                            alt={firstImage.imageName}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveArtifact(artifact.artifactId, shotId);
                            }}
                            sx={{
                              position: "absolute",
                              top: -4,
                              right: -4,
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              backgroundColor: "#252A38",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              border: "1px solid #606479",
                              "&:hover": {
                                backgroundColor: "#3A3F4F",
                              },
                            }}
                          >
                            <X size={10} fill="#fff" />
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  },
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box className={classes.buttonContainer}>
        <Button
          className={classes.cancelButton}
          onClick={handleSequenceThumbnailModal}
        >
          Cancel
        </Button>
        <Button
          className={classes.generateButton}
          endIcon={
            !isThumbnailGenerating && (
              <Chip
                style={{ color: "white", padding: "4px" }}
                avatar={<CoinIconXsmall />}
                label={shotListLength}
                variant="outlined"
              />
            )
          }
          sx={{
            backgroundColor: isThumbnailGenerating
              ? "#606479"
              : "#6C65D9 !important",
          }}
          onClick={() =>
            handleSubmit(selectedPrompt, description, relatedArtifactIds)
          }
          disabled={isThumbnailGenerating}
        >
          {isThumbnailGenerating ? <CircularProgress size={20} /> : "Generate"}
        </Button>
      </Box>
    </Container>
  );
};

export default SequenceThumbnailGenerateModal;
